import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiEnvelope } from '../models/auth.model';

export interface MenuCategory {
image_url: string|null|undefined; id: string; name: string; itemsCount: number; 
}
export interface MenuApiItem {
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  /** lowest price among the item's variants for this branch (customer-paid) */
  price: number;
  flags: string[];
}
export interface MenuSection { id: string; name: string; itemsCount: number; items: MenuApiItem[]; }
export interface BranchMenu { categories: MenuCategory[]; sections: MenuSection[]; }
/** A menu tag (GET /api/v1/tags). `search` tags are the browse chips; `ingredient` tags label contents. */
export interface MenuTag { id: string; name: string; }

/** The browsable menu (categories + items) for one branch — GET /api/v1/menu. */
@Injectable({ providedIn: 'root' })
export class MenuService {
  private readonly base = environment.apiBase;

  constructor(private http: HttpClient) {}

  getMenu(branchId: string): Observable<BranchMenu> {
    const params = new HttpParams().set('branch_id', branchId).set('per_page', '50');
    return this.http.get<ApiEnvelope<any>>(`${this.base}/api/v1/menu`, { params }).pipe(
      map((r) => {
        const d = r.data || {};
        return {
          categories: (d.categories || []).map((c: any): MenuCategory => ({
            id: c.id, name: c.name, itemsCount: c.items_count,
            image_url: c.image_url
          })),
          sections: (d.menu || []).map((s: any): MenuSection => ({
            id: s.id, name: s.name, itemsCount: s.items_count,
            items: (s.items || []).map(mapCard),
          })),
        };
      }),
    );
  }

  /**
   * GET /menu/search — live per-keystroke text search, or filter by a search tag
   * (`search_tag_id`) when the customer taps a browse chip. Both scoped to this branch.
   */
  search(branchId: string, q: string, searchTagIds: string[] = []): Observable<MenuApiItem[]> {
    let params = new HttpParams().set('branch_id', branchId);
    if (q) params = params.set('q', q);
    // One tag keeps the original singular param untouched; several are sent as a
    // repeated array param, which is what the browse chips need for multi-select.
    if (searchTagIds.length === 1) params = params.set('search_tag_id', searchTagIds[0]);
    else searchTagIds.forEach((id) => { params = params.append('search_tag_id[]', id); });
    return this.http.get<ApiEnvelope<any>>(`${this.base}/api/v1/menu/search`, { params }).pipe(
      map((r) => (r.data || []).map(mapCard)),
    );
  }

  /** GET /tags?type=search|ingredient — search chips / ingredient labels. */
  tags(type: 'search' | 'ingredient'): Observable<MenuTag[]> {
    const params = new HttpParams().set('type', type);
    return this.http.get<ApiEnvelope<any>>(`${this.base}/api/v1/tags`, { params }).pipe(
      map((r) => (r.data || []).map((t: any): MenuTag => ({ id: t.id, name: t.name }))),
    );
  }

  /** GET /menu/recent-items — the customer's previously ordered items for this branch. */
  recentItems(branchId: string): Observable<MenuApiItem[]> {
    const params = new HttpParams().set('branch_id', branchId);
    return this.http.get<ApiEnvelope<any>>(`${this.base}/api/v1/menu/recent-items`, { params }).pipe(
      map((r) => (r.data || []).map(mapCard)),
    );
  }
}

/** Map an API item card to MenuApiItem (image_url null → client shows the brand logo). */
function mapCard(i: any): MenuApiItem {
  return {
    id: i.id,
    name: i.name,
    description: i.description ?? null,
    image: i.image_url ?? null,
    price: i.price_from ?? 0,
    flags: i.flags || [],
  };
}
